import * as React from "react";
import { DEFAULT_WIDTHS, MAX_IMAGE_WIDTH, imageDprSet, imageSrcSet, imageVariantUrl, isTransformable, withImageParams } from "~/lib/image-url";

export type PictureProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  /** A URL from `imageUrl()` (not a bare public/ path). */
  src: string;
  /** Served instead of `src` when `desktopMedia` does not match. */
  mobileSrc?: string;
  /** Media query where `src` applies when `mobileSrc` is set. Defaults to Tailwind's lg. */
  desktopMedia?: string;
  /** Rendered width of the image (like `<img sizes>`); enables a responsive width ladder on imgix. */
  sizes?: string;
};

/** The `src`/`srcSet` pair for one image URL. On imgix this is a responsive set; otherwise a plain URL. */
function sourceSet(url: string, sizes: string | undefined, width: number | undefined) {
  if (!isTransformable(url)) return { src: url, srcSet: undefined };
  // Pick the responsive form that matches what we know about the rendered size: a `sizes` string
  // gets a width ladder, a fixed `width` gets 1x/2x/3x, and anything else gets one capped URL —
  // never the multi-megabyte original.
  const srcSet = sizes ? imageSrcSet(url, DEFAULT_WIDTHS) : width ? imageDprSet(url, width) : undefined;
  return { src: withImageParams(url, { w: width ?? MAX_IMAGE_WIDTH, fit: "max" }), srcSet };
}

function variantSources(url: string, media?: string) {
  return (["avif", "webp"] as const).map((ext) => {
    const variant = imageVariantUrl(url, ext);
    return variant ? <source key={ext} media={media} srcSet={variant} type={`image/${ext}`} /> : null;
  });
}

// Drop-in replacement for <img src={x}> where x comes from `imageUrl()`. On imgix the <img> alone
// is enough: auto=format negotiates AVIF/WebP from the Accept header, so there is no <source> per
// format. Without imgix (localdev / pre-cut-over) it falls back to the pre-generated .avif/.webp
// twins, where the build has them (see scripts/images/manifest.mjs).
//
// Lazy by default: an offscreen image isn't fetched until the user nears it, so it can't compete with the
// hero for bandwidth. Pass loading="eager" on an above-the-fold image — above all the page's likely LCP
// element, which Lighthouse flags (`lcp-lazy-loaded`) and e2e/images.spec.ts fails on if it is lazy.
export function Picture({ src, mobileSrc, desktopMedia = "(min-width: 1024px)", alt = "", sizes, ...imgProps }: PictureProps) {
  const width = imgProps.width ? Number(imgProps.width) : undefined;
  const fallback = sourceSet(mobileSrc ?? src, sizes, width);
  const desktop = mobileSrc ? sourceSet(src, sizes, width) : null;
  return (
    // `contents` takes `<picture>` out of the box tree — otherwise a percentage-sized
    // `<img>` (h-full, w-full) resolves against `<picture>`'s own auto-sized box instead
    // of the real layout parent, rendering zoomed/cropped for a mismatched aspect ratio.
    // Its <source> children still count as flex items, so wrap it when the parent is a flex gap.
    <picture className="contents">
      {desktop && (
        <>
          {variantSources(src, desktopMedia)}
          <source media={desktopMedia} srcSet={desktop.srcSet ?? desktop.src} sizes={desktop.srcSet ? sizes : undefined} />
        </>
      )}
      {variantSources(mobileSrc ?? src)}
      {/* eslint-disable-next-line local/no-raw-img -- this is Picture's own <img> */}
      <img src={fallback.src} srcSet={fallback.srcSet} sizes={fallback.srcSet ? sizes : undefined} alt={alt} loading="lazy" decoding="async" {...imgProps} />
    </picture>
  );
}
